import { ApiError, CM_BUILTIN_EDGES, CM_ITSM_PRESET_NAME_MAX, CM_ITSM_PRESETS_MAX, CM_STATUS_KEY_MAX, CM_STATUS_SLUG_PATTERN, cmItsmSchema } from '@bloo-team/api-client'
import type {
  CaseSettingsApi, CmEvidenceSettings, CmFeatureFlag, CmFlagState, CmGrouping, CmItsmConfig, CmItsmOutbox, CmItsmPreset, CmItsmPresetInput, CmItsmSettings,
  CmItsmWebhook, CmStatus, CmStatusFlag,
} from '@bloo-team/api-client'
import { demoCasesInStatus, demoCatalogue, demoEvidenceEnv, demoItsmEnv, demoStatusStore, type DemoStatusDef } from './case-mgmt-demo'
import { DEMO_ITSM_SCHEMA } from './case-settings-demo-schema'
import { payloadRefusalCode } from './itsm-payload'
import { DEMO_NOW } from './signals-demo'

/**
 * The demo case-management settings — the same CaseSettingsApi the live
 * client implements, in memory (docs/cases.md §Settings · Demo mode). It
 * replays the service's rules: built-in statuses are fixed, custom keys are
 * `c_<slug>`, writes need `custom_statuses`, the two closure flags need
 * `workflow_templates`, a change may not leave a status without a route to
 * a closed status, a configured ITSM connection saves edits to pending,
 * secrets never come back, verification waits for the test ticket.
 */

const MIN = 60_000
const fail = (status: number, detail: string) => new ApiError(status, detail, { detail }, detail)

const FLAG_DEFAULTS: Record<CmFeatureFlag, boolean> = {
  custom_statuses: false,
  itsm_auto_sync: true,
  workflow_templates: false,
  evidence_attachments: false,
  sla_tracking: false,
}
const OPERATOR_FLAGS: readonly CmFeatureFlag[] = ['evidence_attachments', 'sla_tracking']

/**
 * Demo starts with custom statuses and workflow templates on, so both editors
 * are live, and with evidence attachments on (the operator's switch) so the
 * case Evidence tab works.
 */
const seedFlags = (): Record<CmFeatureFlag, boolean> => ({ ...FLAG_DEFAULTS, custom_statuses: true, workflow_templates: true, evidence_attachments: true })

const SECRET_FIELDS = ['webhook_auth_value', 'webhook_headers']

interface DemoWebhook {
  w: Omit<CmItsmWebhook, 'callbackToken' | 'savedTo'>
  /** Secrets, kept apart: never returned. */
  secrets: Record<string, string>
  linkedCases: number
}

const JIRA_CONFIG: CmItsmConfig = {
  config_name: 'Jira production',
  webhook_provider: 'jira',
  webhook_method: 'POST',
  webhook_url: 'https://gotham-sec.atlassian.net/rest/api/2/issue',
  webhook_auth_type: 'Basic',
  webhook_body_format: 'json',
  webhook_payload: '{"fields": {"project": {"key": "SEC"}, "summary": "$case_name", "description": "$signal_name", "issuetype": {"name": "Incident"}}}',
  webhook_ticket_id_field: 'key',
  webhook_update_url: 'https://gotham-sec.atlassian.net/rest/api/2/issue/$ticket_id',
  webhook_update_method: 'PUT',
  webhook_update_payload: '{"fields": {"summary": "$case_name"}}',
  field_mappings_status: { 'To Do': 'OPEN', 'In Progress': 'IN-PROGRESS', Waiting: 'ON-HOLD', Done: 'CLOSED' },
  webhook_notes_url: 'https://gotham-sec.atlassian.net/rest/api/2/issue/$ticket_id/comment',
  webhook_notes_method: 'POST',
  webhook_notes_payload: '{"body": "$note_text"}',
}

const ME_CONFIG: CmItsmConfig = {
  config_name: 'ManageEngine (draft)',
  webhook_provider: 'manage_engine',
  webhook_method: 'POST',
  webhook_url: 'https://sdp.gotham.example/api/v3/requests',
  webhook_auth_type: 'ApiKey',
  webhook_auth_header: 'authtoken',
  webhook_body_format: 'form',
  webhook_form_param: 'input_data',
  webhook_payload: '{"request": {"subject": "$case_name", "priority": {"name": "$severity"}}}',
  webhook_ticket_id_field: 'request.id',
}

type Run = NonNullable<CmItsmWebhook['verification']>
/** A run's note fields and steps, as the service reports them (`note_received_at` is not kept once verified). */
const runSteps = (state: Run['state'], ticketCreated: boolean, noteReceived = false): Pick<Run, 'noteReceived' | 'noteReceivedAt' | 'steps'> => ({
  noteReceived: noteReceived || state === 'verified',
  noteReceivedAt: null,
  steps: { ticketCreated, noteReceived: noteReceived || state === 'verified', closed: state === 'verified' },
})
/** `VERIFY-` and four characters from the service's alphabet (no 0 O 1 I L). */
const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
const verificationCode = () => `VERIFY-${Array.from({ length: 4 }, () => CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)]).join('')}`

function seedWebhooks(): DemoWebhook[] {
  return [
    {
      w: {
        id: '00000000-0000-4000-a000-000000000001',
        name: 'Jira production',
        provider: 'jira',
        config: JIRA_CONFIG,
        pendingConfig: null,
        status: 'CONFIGURED',
        hasPending: false,
        locked: false,
        verification: {
          state: 'verified',
          testTicketId: 'SEC-1180',
          startedAt: DEMO_NOW - 9 * 24 * 60 * MIN,
          verifiedAt: DEMO_NOW - 9 * 24 * 60 * MIN + 40 * MIN,
          lastError: null,
          ...runSteps('verified', true),
        },
        createdAt: DEMO_NOW - 12 * 24 * 60 * MIN,
        updatedAt: DEMO_NOW - 9 * 24 * 60 * MIN,
      },
      secrets: { webhook_auth_value: 'svc-bloo:••••' },
      linkedCases: 6,
    },
    {
      w: {
        id: '00000000-0000-4000-a000-000000000002',
        name: 'ManageEngine (draft)',
        provider: 'manage_engine',
        config: ME_CONFIG,
        pendingConfig: null,
        status: 'NOT_CONFIGURED',
        hasPending: false,
        locked: false,
        verification: null,
        createdAt: DEMO_NOW - 2 * 24 * 60 * MIN,
        updatedAt: DEMO_NOW - 2 * 24 * 60 * MIN,
      },
      secrets: {},
      linkedCases: 0,
    },
  ]
}

type OutboxRow = CmItsmOutbox['items'][number]
function seedOutbox(): OutboxRow[] {
  const jira = '00000000-0000-4000-a000-000000000001'
  let n = 0
  const row = (i: number, event: string, status: OutboxRow['status'], ago: number, error: string | null = null, attempts = 1): OutboxRow => ({
    id: `00000000-0000-4000-b000-${String(++n).padStart(12, '0')}`,
    caseId: `00000000-0000-4000-8000-${(0xa21e + i).toString(16).padStart(12, '0')}`,
    configId: jira,
    event,
    status,
    attempts,
    nextAttemptAt: status === 'failed' || status === 'done' ? null : DEMO_NOW - ago * MIN + 2 * MIN,
    lastError: error,
    createdAt: DEMO_NOW - ago * MIN,
    updatedAt: DEMO_NOW - ago * MIN,
  })
  return [
    row(0, 'status_changed', 'done', 14),
    row(0, 'note_added', 'done', 22),
    row(1, 'assignment_changed', 'failed', 41, 'HTTP 401 from https://gotham-sec.atlassian.net — check the credentials', 5),
    // The LSASS case's ticket: one update failed for good before the later ones got through.
    row(0, 'note_added', 'failed', 180, 'HTTP 503 from https://gotham-sec.atlassian.net — service unavailable', 5),
    row(2, 'signals_attached', 'pending', 3, null, 0),
    row(3, 'status_changed', 'done', 95),
  ]
}

const seedGrouping = (): CmGrouping => ({
  intervalMinutes: 15,
  addSimilar: true,
  similarWindowMinutes: 1440,
  relationsEnabled: true,
  relationWindowDays: 14,
  relationMinShared: 1,
  relationEntityTypes: ['host', 'user'],
  ignoreValues: ['localhost', '127.0.0.1', 'SYSTEM'],
  similarMaxPerGroup: null,
})

/** Payload presets (Epic 19) on the demo Jira connection, by webhook id. */
const seedPresets = (): Record<string, CmItsmPreset[]> => {
  const jira = '00000000-0000-4000-a000-000000000001'
  const at = DEMO_NOW - 3 * 24 * 60 * MIN
  const p = (n: number, name: string, payload: Record<string, unknown>): CmItsmPreset => ({
    id: `00000000-0000-4000-b000-${String(n).padStart(12, '0')}`,
    webhookId: jira,
    name,
    payload,
    createdBy: 'Lucius Fox',
    createdAt: at,
    updatedAt: at,
  })
  return {
    [jira]: [
      p(1, 'Network team', { fields: { customfield_10042: 'NET', components: [{ name: 'Network' }] } }),
      p(2, 'P1 urgent', { fields: { priority: { name: 'Highest' } }, labels: ['urgent'] }),
      p(3, 'SOC queue', { fields: { customfield_10050: 'SOC-L2' }, labels: ['soc'] }),
    ],
  }
}

const PLATFORM_QUOTA = 50 * 1_048_576
const PLATFORM_TYPES = ['pdf', 'csv', 'json']
const seedEvidence = () => ({ retentionDays: 30, quotaBytes: null as number | null, allowedTypes: null as string[] | null })

const state = {
  flags: seedFlags(),
  itsmEnabled: { enabled: true, updatedAt: DEMO_NOW - 12 * 24 * 60 * MIN, updatedBy: 'Lucius Fox' } as CmItsmSettings,
  webhooks: seedWebhooks(),
  presets: seedPresets(),
  outbox: seedOutbox(),
  grouping: seedGrouping(),
  evidence: seedEvidence(),
  seq: 0,
}

/** Test hook. */
export function resetDemoCaseSettings() {
  state.flags = seedFlags()
  state.itsmEnabled = { enabled: true, updatedAt: DEMO_NOW - 12 * 24 * 60 * MIN, updatedBy: 'Lucius Fox' }
  state.webhooks = seedWebhooks()
  state.presets = seedPresets()
  state.outbox = seedOutbox()
  state.grouping = seedGrouping()
  state.evidence = seedEvidence()
  state.seq = 0
}

/* ---------------------------------------------------------------- statuses */

function requireCustomStatuses() {
  if (!state.flags.custom_statuses) throw fail(403, 'Custom statuses are not enabled for this tenant.')
}

function checkFlags(flags: Partial<Record<CmStatusFlag, boolean>> | undefined) {
  if ((flags?.require_resolution_reason || flags?.require_root_cause) && !state.flags.workflow_templates) {
    throw fail(403, 'Workflow templates are not enabled for this tenant.')
  }
}

const toFlags = (f: Partial<Record<CmStatusFlag, boolean>> = {}) => ({
  requireReason: f.require_reason === true,
  requireAssignee: f.require_assignee === true,
  requireNote: f.require_note === true,
  requireResolutionReason: f.require_resolution_reason === true,
  requireRootCause: f.require_root_cause === true,
})

/**
 * The dead-end guard: run `change` on the store, then refuse (and undo) if
 * an active custom status can no longer reach a closed status, or an
 * archived one has no way out.
 */
function guarded<T>(what: string, change: () => T): T {
  const before = { defs: demoStatusStore.defs.map((d) => ({ ...d })), edges: [...demoStatusStore.edges] }
  const out = change()
  const cat = demoCatalogue(true)
  const closed = new Set(cat.filter((s) => s.category === 'closed').map((s) => s.key))
  const next = new Map(cat.map((s) => [s.key, s.allowedNext]))
  const reaches = (k: string) => {
    const seen = new Set([k])
    const queue = [k]
    while (queue.length) {
      const x = queue.shift()!
      if (closed.has(x) && x !== k) return true
      for (const y of next.get(x) ?? []) {
        if (seen.has(y)) continue
        seen.add(y)
        queue.push(y)
      }
    }
    return false
  }
  const stuck = cat.filter((s) => !s.builtin && !s.archived && s.category !== 'closed' && !reaches(s.key)).map((s) => s.key)
  const noExit = cat.filter((s) => !s.builtin && s.archived && s.allowedNext.length === 0).map((s) => s.key)
  if (stuck.length || noExit.length) {
    demoStatusStore.defs = before.defs
    demoStatusStore.edges = before.edges
    throw fail(
      409,
      stuck.length
        ? `${what} would leave status(es) with no route to a closed status: ${stuck.join(', ')}`
        : `${what} would leave archived status(es) without an outgoing transition: ${noExit.join(', ')}`,
    )
  }
  return out
}

const row = (key: string): CmStatus => {
  const s = demoCatalogue(true).find((x) => x.key === key)
  if (!s) throw fail(404, 'Status not found')
  return s
}
const def = (key: string): DemoStatusDef => {
  const d = demoStatusStore.defs.find((x) => x.key === key)
  if (!d) throw fail(404, 'Status not found')
  return d
}
const putDef = (key: string, fn: (d: DemoStatusDef) => DemoStatusDef) => {
  demoStatusStore.defs = demoStatusStore.defs.map((d) => (d.key === key ? fn(d) : d))
}

const slugOf = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')

/** The service's three closure presets: flags set on every active custom status of a category. */
const PRESETS: { name: string; title: string; description: string; category: CmStatus['category']; flags: (keyof ReturnType<typeof toFlags>)[] }[] = [
  {
    name: 'closure-requires-reason',
    title: 'Closing needs a resolution reason',
    description: 'Every custom closed status requires a resolution reason.',
    category: 'closed',
    flags: ['requireResolutionReason'],
  },
  {
    name: 'closure-requires-reason-and-root-cause',
    title: 'Closing needs a reason and a root cause',
    description: 'Every custom closed status requires a resolution reason and a root cause.',
    category: 'closed',
    flags: ['requireResolutionReason', 'requireRootCause'],
  },
  {
    name: 'hold-requires-note',
    title: 'Holding needs a note',
    description: 'Every custom on-hold status requires a note.',
    category: 'on_hold',
    flags: ['requireNote'],
  },
]
const presetTargets = (p: (typeof PRESETS)[number]) => demoStatusStore.defs.filter((d) => !d.builtin && !d.archived && d.category === p.category)
const presetState = (p: (typeof PRESETS)[number]) => {
  const t = presetTargets(p)
  if (!t.length) return 'not_applicable' as const
  const on = t.filter((d) => p.flags.every((f) => d[f])).length
  return on === t.length ? ('applied' as const) : on ? ('partial' as const) : ('not_applied' as const)
}
const FLAG_WIRE: Record<string, string> = {
  requireResolutionReason: 'require_resolution_reason',
  requireRootCause: 'require_root_cause',
  requireNote: 'require_note',
}

/* -------------------------------------------------------------------- ITSM */

const hook = (id: string) => {
  const x = state.webhooks.find((y) => y.w.id === id)
  if (!x) throw fail(404, 'ITSM webhook not found')
  return x
}

/** The presets API's errors carry a code: `{"detail": {"code", "message"}}`. */
const failCode = (status: number, code: string, message: string) => new ApiError(status, message, { detail: { code, message } }, message)
const presetsOf = (webhookId: string) => {
  if (!state.webhooks.some((y) => y.w.id === webhookId)) throw failCode(404, 'webhook_not_found', 'Webhook not found')
  return state.presets[webhookId] ?? []
}
const byName = (a: CmItsmPreset, b: CmItsmPreset) => a.name.localeCompare(b.name)
/** Name 1–100 characters, unique per connection; the payload within the Epic 17 caps — as the service checks. */
function checkPreset(webhookId: string, i: CmItsmPresetInput, self: string | null) {
  const name = i.name.trim()
  if (!name || name.length > CM_ITSM_PRESET_NAME_MAX) throw fail(422, `name: must be 1 to ${CM_ITSM_PRESET_NAME_MAX} characters`)
  if (payloadRefusalCode(i.payload)) throw failCode(422, 'preset_invalid', 'Preset name or payload is invalid')
  if (presetsOf(webhookId).some((p) => p.id !== self && p.name === name)) throw failCode(409, 'preset_name_taken', 'Preset name already used for this webhook')
  return name
}
const putHook = (id: string, fn: (x: DemoWebhook) => DemoWebhook) => {
  state.webhooks = state.webhooks.map((x) => (x.w.id === id ? fn(x) : x))
}
/** Secrets come back blank, as the service masks them. */
const masked = (c: CmItsmConfig, secrets: Record<string, string>): CmItsmConfig => ({
  ...c,
  ...Object.fromEntries(SECRET_FIELDS.filter((k) => k in c || k in secrets).map((k) => [k, ''])),
})
const publicHook = (x: DemoWebhook): CmItsmWebhook => ({
  ...x.w,
  config: masked(x.w.config, x.secrets),
  pendingConfig: x.w.pendingConfig ? masked(x.w.pendingConfig, x.secrets) : null,
  callbackToken: null,
  savedTo: null,
})
const token = () => `whk_demo_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`

/**
 * The customer's side of a verification (Epic 20) is out of the console's
 * hands; demo mode plays it: the note with the run's code 10 seconds after
 * verification starts, the test ticket closed at 20.
 */
const VERIFY_NOTE_MS = 10_000
const VERIFY_SETTLE_MS = 20_000
function settleVerification() {
  const now = Date.now()
  state.webhooks = state.webhooks.map((x) => {
    const v = x.w.verification
    if (v?.state !== 'waiting' || v.startedAt === null || now - v.startedAt < VERIFY_NOTE_MS) return x
    if (now - v.startedAt < VERIFY_SETTLE_MS) {
      return v.noteReceived ? x : { ...x, w: { ...x.w, verification: { ...v, ...runSteps('waiting', true, true), noteReceivedAt: v.startedAt + VERIFY_NOTE_MS } } }
    }
    return {
      ...x,
      w: {
        ...x.w,
        config: x.w.pendingConfig ?? x.w.config,
        pendingConfig: null,
        hasPending: false,
        locked: false,
        status: 'CONFIGURED',
        verification: { ...v, state: 'verified', verifiedAt: now, ...runSteps('verified', true) },
        updatedAt: now,
      },
    }
  })
}

function validateConfig(provider: string, config: CmItsmConfig) {
  const schema = cmItsmSchema.parse(DEMO_ITSM_SCHEMA)
  if (!schema.providers.some((p) => p.value === provider)) throw fail(422, `provider: must be one of [${schema.providers.map((p) => p.value).join(', ')}]`)
  const visible = (name: string) => {
    const f = schema.fields.find((x) => x.name === name)
    if (!f?.visibleWhen) return true
    const v = config[f.visibleWhen.field]
    return typeof v === 'string' && f.visibleWhen.values.includes(v)
  }
  for (const f of schema.fields) {
    if (!f.required || f.sensitive || !visible(f.name)) continue
    const v = config[f.name]
    if (typeof v !== 'string' || !v.trim()) throw fail(422, `config.${f.name}: is required`)
  }
  for (const k of ['webhook_url', 'webhook_update_url', 'webhook_notes_url']) {
    const v = config[k]
    if (typeof v === 'string' && /^https?:\/\/(localhost|127\.|10\.|192\.168\.)/.test(v)) throw fail(400, 'A configured URL is not permitted')
  }
}

/** Split a submitted config into the stored part and the secrets (blank = keep). */
function splitSecrets(config: CmItsmConfig, current: Record<string, string>) {
  const rest: CmItsmConfig = {}
  const secrets = { ...current }
  for (const [k, v] of Object.entries(config)) {
    if (k === 'config_id') continue
    if (SECRET_FIELDS.includes(k)) {
      if (typeof v === 'string' && v.trim()) secrets[k] = v
    } else rest[k] = v
  }
  return { rest, secrets }
}

/* --------------------------------------------------------------------- api */

export const demoCaseSettingsApi: CaseSettingsApi = {
  statuses: async (_s, o = {}) => demoCatalogue(o.includeArchived === true),
  createStatus: async (_s, i) => {
    requireCustomStatuses()
    checkFlags(i.flags)
    const label = i.label.trim()
    if (!label || label.length > 100) throw fail(422, 'label: must be 1 to 100 characters')
    const slug = i.slug?.trim() || slugOf(label)
    if (!CM_STATUS_SLUG_PATTERN.test(slug)) throw fail(422, 'slug: lowercase letters, digits and single underscores')
    const key = `c_${slug}`
    if (key.length > CM_STATUS_KEY_MAX) throw fail(422, `slug: the key c_${slug} is longer than ${CM_STATUS_KEY_MAX} characters`)
    if (['new', 'in_progress', 'on_hold', 'closed', 'in-progress', 'on-hold'].includes(slug)) throw fail(409, 'slug is reserved for a built-in status')
    if (demoStatusStore.defs.some((d) => d.key.toLowerCase() === key.toLowerCase())) throw fail(409, 'A status with this key already exists')
    if (demoStatusStore.defs.filter((d) => !d.builtin).length >= 100) throw fail(409, 'A tenant can have at most 100 custom statuses')
    guarded('Creating this status', () => {
      demoStatusStore.defs = [
        ...demoStatusStore.defs,
        { key, label, category: i.category, builtin: false, archived: false, order: i.order ?? 0, ...toFlags(i.flags) },
      ]
    })
    return row(key)
  },
  updateStatus: async (_s, key, p) => {
    requireCustomStatuses()
    if (def(key).builtin) throw fail(409, 'built-in statuses cannot be modified')
    checkFlags(p.flags)
    if (p.label !== undefined && (!p.label.trim() || p.label.trim().length > 100)) throw fail(422, 'label: must be 1 to 100 characters')
    if (p.category !== undefined && p.category !== def(key).category) {
      const n = demoCasesInStatus(key)
      if (n) throw fail(409, `category cannot change while ${n} case(s) use this status; archive it and create a new status instead`)
    }
    guarded('Changing this status', () =>
      putDef(key, (d) => ({
        ...d,
        ...(p.label !== undefined ? { label: p.label.trim() } : {}),
        ...(p.category !== undefined ? { category: p.category } : {}),
        ...(p.order !== undefined ? { order: p.order } : {}),
        ...(p.flags !== undefined ? toFlags(p.flags) : {}),
      })),
    )
    return row(key)
  },
  archiveStatus: async (_s, key) => {
    requireCustomStatuses()
    if (def(key).builtin) throw fail(409, 'built-in statuses cannot be modified')
    guarded('Archiving this status', () => putDef(key, (d) => ({ ...d, archived: true })))
    return { ...row(key), casesInStatus: demoCasesInStatus(key) }
  },
  unarchiveStatus: async (_s, key) => {
    requireCustomStatuses()
    if (def(key).builtin) throw fail(409, 'built-in statuses cannot be modified')
    putDef(key, (d) => ({ ...d, archived: false }))
    return row(key)
  },
  edges: async () => demoStatusStore.edges.map((e) => ({ ...e })),
  setEdge: async (_s, e) => {
    requireCustomStatuses()
    if (e.from === e.to) throw fail(422, 'a status cannot transition to itself')
    const from = def(e.from)
    const to = def(e.to)
    if (from.builtin && to.builtin && CM_BUILTIN_EDGES.some(([a, b]) => a === e.from && b === e.to)) {
      throw fail(422, 'transitions between built-in statuses cannot be changed')
    }
    if (e.kind === 'allow' && to.archived) throw fail(422, 'cannot allow a transition into an archived status')
    if (demoStatusStore.edges.length >= 500 && !demoStatusStore.edges.some((x) => x.from === e.from && x.to === e.to)) {
      throw fail(409, 'A tenant can have at most 500 transition rules')
    }
    guarded(e.kind === 'block' ? 'Blocking this transition' : 'Allowing this transition', () => {
      demoStatusStore.edges = [...demoStatusStore.edges.filter((x) => !(x.from === e.from && x.to === e.to)), { ...e }]
    })
    return { ...e }
  },
  presets: async () => ({
    enabled: state.flags.workflow_templates,
    presets: PRESETS.map((p) => ({
      name: p.name,
      title: p.title,
      description: p.description,
      applicable: presetTargets(p).length > 0,
      state: presetState(p),
      targets: presetTargets(p).map((d) => d.key),
    })),
  }),
  runPreset: async (_s, name, action, dryRun) => {
    if (!state.flags.workflow_templates) throw fail(403, 'Workflow templates are not enabled for this tenant.')
    const p = PRESETS.find((x) => x.name === name)
    if (!p) throw fail(404, 'Workflow preset not found')
    const value = action === 'apply'
    const changes = presetTargets(p)
      .filter((d) => p.flags.some((f) => d[f] !== value))
      .map((d) => ({
        key: d.key,
        before: Object.fromEntries(p.flags.map((f) => [FLAG_WIRE[f]!, d[f]])),
        after: Object.fromEntries(p.flags.map((f) => [FLAG_WIRE[f]!, value])),
      }))
    const skipped = demoStatusStore.defs
      .filter((d) => d.category === p.category && (d.builtin || d.archived))
      .map((d) => ({ key: d.key, reason: d.builtin ? 'built_in_immutable' : 'archived' }))
    if (!dryRun) for (const c of changes) putDef(c.key, (d) => ({ ...d, ...Object.fromEntries(p.flags.map((f) => [f, value])) }))
    return { name, action, dry_run: dryRun, dryRun, applicable: presetTargets(p).length > 0, changed: changes.length > 0, changes, skipped }
  },
  features: async () =>
    Object.fromEntries(
      (Object.keys(FLAG_DEFAULTS) as CmFeatureFlag[]).map((f): [string, CmFlagState] => [
        f,
        { enabled: state.flags[f], default: FLAG_DEFAULTS[f], overridden: state.flags[f] !== FLAG_DEFAULTS[f], operatorManaged: OPERATOR_FLAGS.includes(f) },
      ]),
    ),
  setFeature: async (_s, flag, enabled) => {
    if (!(flag in FLAG_DEFAULTS)) throw fail(404, 'Unknown feature flag')
    if (OPERATOR_FLAGS.includes(flag)) throw fail(403, 'This feature flag is managed by the platform operator')
    state.flags = { ...state.flags, [flag]: enabled }
    return { enabled, default: FLAG_DEFAULTS[flag], overridden: enabled !== FLAG_DEFAULTS[flag], operatorManaged: false }
  },
  itsmSchema: async () => cmItsmSchema.parse(DEMO_ITSM_SCHEMA),
  itsmSettings: async () => ({ ...state.itsmEnabled }),
  setItsmEnabled: async (_s, enabled) => {
    if (enabled !== state.itsmEnabled.enabled) state.itsmEnabled = { enabled, updatedAt: Date.now(), updatedBy: 'Demo Analyst' }
    return { ...state.itsmEnabled }
  },
  itsmPresets: async (_s, w) => [...presetsOf(w)].sort(byName),
  createItsmPreset: async (_s, w, i) => {
    const name = checkPreset(w, i, null)
    if (presetsOf(w).length >= CM_ITSM_PRESETS_MAX) throw failCode(409, 'preset_limit_reached', 'Webhook already has 20 presets')
    const now = Date.now()
    const p: CmItsmPreset = { id: `00000000-0000-4000-b000-${String(100 + ++state.seq).padStart(12, '0')}`, webhookId: w, name, payload: i.payload, createdBy: 'Demo Analyst', createdAt: now, updatedAt: now }
    state.presets = { ...state.presets, [w]: [...presetsOf(w), p] }
    return p
  },
  updateItsmPreset: async (_s, w, id, i) => {
    const old = presetsOf(w).find((p) => p.id === id)
    if (!old) throw failCode(404, 'preset_not_found', 'Preset not found')
    const p: CmItsmPreset = { ...old, name: checkPreset(w, i, id), payload: i.payload, updatedAt: Date.now() }
    state.presets = { ...state.presets, [w]: presetsOf(w).map((x) => (x.id === id ? p : x)) }
    return p
  },
  deleteItsmPreset: async (_s, w, id) => {
    if (!presetsOf(w).some((p) => p.id === id)) throw failCode(404, 'preset_not_found', 'Preset not found')
    state.presets = { ...state.presets, [w]: presetsOf(w).filter((p) => p.id !== id) }
  },
  webhooks: async () => {
    settleVerification()
    return state.webhooks.map(publicHook)
  },
  webhook: async (_s, id) => {
    settleVerification()
    return publicHook(hook(id))
  },
  createWebhook: async (_s, i) => {
    const name = i.name.trim()
    if (!name || name.length > 255) throw fail(422, 'name: must be 1 to 255 characters')
    if (state.webhooks.some((x) => x.w.name.toLowerCase() === name.toLowerCase())) throw fail(409, 'An ITSM webhook with this name already exists')
    validateConfig(i.provider, i.config)
    const { rest, secrets } = splitSecrets({ ...i.config, webhook_provider: i.provider }, {})
    const now = Date.now()
    const id = `00000000-0000-4000-a000-${String(100 + ++state.seq).padStart(12, '0')}`
    const x: DemoWebhook = {
      w: { id, name, provider: i.provider, config: rest, pendingConfig: null, status: 'NOT_CONFIGURED', hasPending: false, locked: false, verification: null, createdAt: now, updatedAt: now },
      secrets,
      linkedCases: 0,
    }
    state.webhooks = [...state.webhooks, x]
    return { ...publicHook(x), callbackToken: token() }
  },
  updateWebhook: async (_s, id, i) => {
    const x = hook(id)
    if (x.w.locked || x.w.verification?.state === 'waiting') throw fail(423, 'Verification in progress — complete or cancel before editing.')
    if (x.w.status === 'CONFIGURED' && i.provider !== x.w.provider) {
      throw fail(409, 'The provider of a configured webhook cannot be changed; create a new webhook instead')
    }
    const name = i.name.trim()
    if (state.webhooks.some((y) => y.w.id !== id && y.w.name.toLowerCase() === name.toLowerCase())) throw fail(409, 'An ITSM webhook with this name already exists')
    validateConfig(i.provider, i.config)
    const { rest, secrets } = splitSecrets({ ...i.config, webhook_provider: i.provider }, x.secrets)
    const now = Date.now()
    const toPending = x.w.status === 'CONFIGURED'
    putHook(id, (y) => ({
      ...y,
      secrets,
      w: {
        ...y.w,
        name,
        provider: i.provider,
        ...(toPending ? { pendingConfig: rest, hasPending: true } : { config: rest, status: 'NOT_CONFIGURED' as const }),
        updatedAt: now,
      },
    }))
    return { ...publicHook(hook(id)), savedTo: toPending ? 'pending' : 'config' }
  },
  verifyWebhook: async (_s, id) => {
    const x = hook(id)
    if (x.w.verification?.state === 'waiting') throw fail(409, 'Verification already in progress.')
    if (x.w.status === 'CONFIGURED' && !x.w.hasPending) throw fail(409, 'Nothing to verify: save a change to this webhook first.')
    const live = x.w.pendingConfig ?? x.w.config
    if (typeof live.webhook_url === 'string' && live.webhook_url.includes('fail')) {
      putHook(id, (y) => ({
        ...y,
        w: { ...y.w, status: y.w.status === 'CONFIGURED' ? 'CONFIGURED' : 'FAILED', verification: { state: 'failed', testTicketId: null, startedAt: Date.now(), verifiedAt: null, lastError: 'create: HTTP 404 from the create URL', ...runSteps('failed', false) } },
      }))
      throw new ApiError(400, 'create: HTTP 404 from the create URL', { result: 'failed', error: 'create: HTTP 404 from the create URL' })
    }
    const ticket = `${x.w.provider === 'jira' ? 'SEC' : 'REQ'}-${1200 + state.seq++}`
    putHook(id, (y) => ({
      ...y,
      w: {
        ...y.w,
        status: y.w.status === 'CONFIGURED' ? 'CONFIGURED' : 'WAITING_CALLBACK',
        locked: true,
        verification: { state: 'waiting', testTicketId: ticket, startedAt: Date.now(), verifiedAt: null, lastError: null, ...runSteps('waiting', true) },
      },
    }))
    return {
      result: 'waiting',
      status: hook(id).w.status,
      testTicketId: ticket,
      message: 'Test ticket created. Ask the customer to send a note with the code, then close it.',
      code: verificationCode(),
    }
  },
  cancelVerify: async (_s, id) => {
    const x = hook(id)
    if (x.w.verification?.state !== 'waiting') throw fail(409, 'Nothing to cancel.')
    putHook(id, (y) => ({
      ...y,
      w: { ...y.w, status: y.w.status === 'CONFIGURED' ? 'CONFIGURED' : 'NOT_CONFIGURED', locked: false, verification: null },
    }))
    return { result: 'cancelled', status: hook(id).w.status, testTicketId: null, message: 'Verification cancelled. The test ticket in your ITSM was not closed.', code: null }
  },
  rotateToken: async (_s, id) => {
    hook(id)
    return { callbackToken: token(), note: 'The previous token stopped working.' }
  },
  outbox: async (_s, q) => {
    const rows = state.outbox.filter((r) => !q.status || r.status === q.status).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
    const count = (st: OutboxRow['status']) => state.outbox.filter((r) => r.status === st).length
    return {
      items: rows.slice(q.offset, q.offset + q.limit),
      counts: { pending: count('pending'), in_flight: count('in_flight'), done: count('done'), failed: count('failed') },
      limit: q.limit,
      offset: q.offset,
    }
  },
  retryOutbox: async (_s, rowId) => {
    const r = state.outbox.find((x) => x.id === rowId)
    if (!r) throw fail(404, 'Outbox row not found.')
    if (r.status !== 'failed') throw fail(409, 'Only failed rows can be retried.')
    state.outbox = state.outbox.map((x) => (x.id === rowId ? { ...x, status: 'pending', attempts: 0, nextAttemptAt: Date.now(), updatedAt: Date.now() } : x))
  },
  grouping: async () => ({ ...state.grouping }),
  updateGrouping: async (_s, patch) => {
    if (patch.relationEntityTypes !== undefined && patch.relationEntityTypes.length === 0) throw fail(422, 'relation_entity_types: choose at least one')
    if (patch.relationMinShared !== undefined && (patch.relationMinShared < 1 || patch.relationMinShared > 10)) throw fail(422, 'relation_min_shared: 1 to 10')
    if (patch.ignoreValues !== undefined && patch.ignoreValues.length > 500) throw fail(422, 'ignore_values: at most 500 values')
    state.grouping = { ...state.grouping, ...patch }
    return { ...state.grouping }
  },
  evidence: async () => evidenceView(),
  updateEvidence: async (_s, p) => {
    if (p.retentionDays !== undefined && (p.retentionDays < 1 || p.retentionDays > 365)) throw fail(422, 'retention_days must be between 1 and 365')
    if (p.quotaBytes !== undefined && p.quotaBytes !== null && (p.quotaBytes < 1_048_576 || p.quotaBytes > PLATFORM_QUOTA)) {
      throw fail(422, `quota_bytes must be between 1 MiB and ${PLATFORM_QUOTA / 1_048_576} MiB`)
    }
    if (p.allowedTypes && (p.allowedTypes.length === 0 || p.allowedTypes.some((t) => !PLATFORM_TYPES.includes(t)))) {
      throw fail(422, 'allowed_types must be a non-empty subset of the platform types')
    }
    state.evidence = { ...state.evidence, ...p }
    return evidenceView()
  },
}

function evidenceView(): CmEvidenceSettings {
  const e = state.evidence
  return {
    retentionDays: e.retentionDays,
    quotaBytes: e.quotaBytes,
    allowedTypes: e.allowedTypes,
    effectiveQuotaBytes: e.quotaBytes ?? PLATFORM_QUOTA,
    effectiveAllowedTypes: e.allowedTypes ?? PLATFORM_TYPES,
    platformQuotaBytes: PLATFORM_QUOTA,
    quotaCeilingBytes: PLATFORM_QUOTA,
    quotaSource: e.quotaBytes !== null ? 'tenant' : 'platform',
    platformAllowedTypes: PLATFORM_TYPES,
    minQuotaBytes: 1_048_576,
    minRetentionDays: 1,
    maxRetentionDays: 365,
  }
}

// The case side's ITSM sync reads the tenant switch and the connections from here.
demoItsmEnv.enabled = () => state.itsmEnabled.enabled
demoItsmEnv.connection = (id) => {
  const x = state.webhooks.find((y) => y.w.id === id)
  return x ? { status: x.w.status, provider: x.w.provider } : null
}
demoItsmEnv.presets = (id) => state.presets[id] ?? []
demoItsmEnv.outbox = (caseId, configId) => state.outbox.filter((r) => r.caseId === caseId && r.configId === configId)

// The case Evidence tab reads the operator's switch and the tenant's allowed types from here.
demoEvidenceEnv.enabled = () => state.flags.evidence_attachments
demoEvidenceEnv.allowedTypes = () => state.evidence.allowedTypes ?? PLATFORM_TYPES
demoEvidenceEnv.quotaBytes = () => state.evidence.quotaBytes ?? PLATFORM_QUOTA
